
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import { apiClient } from '@/lib/api-client'
import type {
  UserDietProfile,
  CreateUserDietProfileData,
  WeightLog,
  DailyIntake,
  FastingLog,
} from '@/types/diet-v2'

// ============================================
// USER DIET PROFILE
// ============================================

// Get user diet profile
export function useUserDietProfile(userId: string) {
  return useQuery<UserDietProfile | null>({
    queryKey: ['userDietProfile', userId],
    queryFn: async () => {
      try {
        const response = await apiClient.get(`/user-diet-profile/${userId}`)
        return response.data
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!userId,
  })
}

// Create or update user diet profile
export function useCreateOrUpdateUserDietProfile() {
  const queryClient = useQueryClient()

  return useMutation<UserDietProfile, Error, CreateUserDietProfileData>({
    mutationFn: async (data) => {
      const response = await apiClient.post('/user-diet-profile', data)
      return response.data
    },
    onSuccess: (data) => {
      toast({
        title: 'Success',
        description: 'Diet profile saved successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['userDietProfile', data.userId] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save diet profile',
        variant: 'destructive',
      })
    },
  })
}

// Add weight log
export function useAddWeightLog() {
  const queryClient = useQueryClient()

  return useMutation<WeightLog, Error, { 
    userId: string
    weight: number
    date?: string
    notes?: string 
  }>({
    mutationFn: async ({ userId, weight, date, notes }) => {
      const response = await apiClient.post(`/weight-tracking/${userId}`, {
        weight,
        date,
        notes,
      })
      return response.data
    },
    onSuccess: (_, variables) => {
      toast({
        title: 'Success',
        description: 'Weight log added successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['userDietProfile', variables.userId] })
      queryClient.invalidateQueries({ queryKey: ['weightHistory', variables.userId] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add weight log',
        variant: 'destructive',
      })
    },
  })
}

// Get weight history
export function useWeightHistory(userId: string, startDate?: string, endDate?: string) {
  return useQuery<WeightLog[]>({
    queryKey: ['weightHistory', userId, startDate, endDate],
    queryFn: async () => {
      try {
        const response = await apiClient.get(`/weight-tracking/${userId}`)
        return response.data
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return []
        }
        throw error
      }
    },
    enabled: !!userId,
  })
}

// ============================================
// DAILY INTAKE
// ============================================

// Get daily intake for specific date
export function useDailyIntake(userId: string, date: string) {
  return useQuery<{
    intake: DailyIntake | null
    assignedPlan?: {
      _id: string
      name: string
      dayNumber: number
      plannedCalories: number
      plannedMacros: {
        protein: number
        carbs: number
        fat: number
        fiber: number
      }
    }
  }>({
    queryKey: ['dailyIntake', userId, date],
    queryFn: async () => {
      const response = await apiClient.get(`/daily-intake/${userId}/${date}`)
      return response.data
    },
    enabled: !!userId && !!date,
  })
}

// Get daily intakes range
export function useUserDailyIntakes(userId: string, startDate?: string, endDate?: string) {
  return useQuery<DailyIntake[]>({
    queryKey: ['dailyIntakes', userId, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (startDate) params.append('startDate', startDate)
      if (endDate) params.append('endDate', endDate)
      
      const response = await apiClient.get(`/daily-intake/${userId}/range?${params.toString()}`)
      return response.data
    },
    enabled: !!userId,
  })
}

// Log daily intake
export function useLogDailyIntake() {
  const queryClient = useQueryClient()

  return useMutation<DailyIntake, Error, {
    userId: string
    date: string
    meals: Array<{
      type: string
      time: string
      foods: Array<{
        name: string
        quantity: number
        unit: string
        calories: number
        macros: {
          protein: number
          carbs: number
          fat: number
          fiber: number
        }
      }>
    }>
  }>({
    mutationFn: async (data) => {
      const response = await apiClient.post('/daily-intake', data)
      return response.data
    },
    onSuccess: (data) => {
      toast({
        title: 'Success',
        description: 'Daily intake logged successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['dailyIntakes', data.userId] })
      queryClient.invalidateQueries({ queryKey: ['dailyIntake', data.userId] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to log daily intake',
        variant: 'destructive',
      })
    },
  })
}

// ============================================
// FASTING
// ============================================

// Get user fasting logs
export function useUserFastingLogs(userId: string, startDate?: string, limit?: number) {
  return useQuery<FastingLog[]>({
    queryKey: ['fastingLogs', userId, startDate, limit],
    queryFn: async () => {
      try {
        const params = new URLSearchParams()
        if (startDate) params.append('startDate', startDate)
        if (limit) params.append('limit', String(limit))
        
        const response = await apiClient.get(`/fasting/user/${userId}?${params.toString()}`)
        return response.data
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return []
        }
        throw error
      }
    },
    enabled: !!userId,
  })
}

// Get active fasting
export function useActiveFasting(userId: string) {
  return useQuery<FastingLog | null>({
    queryKey: ['activeFasting', userId],
    queryFn: async () => {
      try {
        const response = await apiClient.get(`/fasting/user/${userId}/active`)
        return response.data
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!userId,
    refetchInterval: 60000, // Refetch every minute for active fast
  })
}

// Start fasting
export function useStartFasting() {
  const queryClient = useQueryClient()

  return useMutation<FastingLog, Error, { 
    userId: string
    fastingType: string
    targetDuration: number 
  }>({
    mutationFn: async (data) => {
      const response = await apiClient.post('/fasting/start', data)
      return response.data
    },
    onSuccess: (data) => {
      toast({
        title: 'Fasting Started',
        description: 'Your fasting period has begun. Good luck!',
      })
      queryClient.invalidateQueries({ queryKey: ['fastingLogs', data.userId] })
      queryClient.invalidateQueries({ queryKey: ['activeFasting', data.userId] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to start fasting',
        variant: 'destructive',
      })
    },
  })
}

// End fasting
export function useEndFasting() {
  const queryClient = useQueryClient()

  return useMutation<FastingLog, Error, { 
    fastingId: string
    userId: string
    notes?: string 
  }>({
    mutationFn: async ({ fastingId, notes }) => {
      const response = await apiClient.post(`/fasting/${fastingId}/end`, { notes })
      return response.data
    },
    onSuccess: (data) => {
      const hours = Math.floor((data.duration || 0) / 60)
      const minutes = (data.duration || 0) % 60
      toast({
        title: 'Fasting Completed',
        description: `Great job! You fasted for ${hours}h ${minutes}m`,
      })
      queryClient.invalidateQueries({ queryKey: ['fastingLogs', data.userId] })
      queryClient.invalidateQueries({ queryKey: ['activeFasting', data.userId] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to end fasting',
        variant: 'destructive',
      })
    },
  })
}

// Get fasting stats
export function useFastingStats(userId: string, startDate?: string, endDate?: string) {
  return useQuery<{
    totalFasts: number
    completedFasts: number
    averageDuration: number
    longestFast: number
    currentStreak: number
    fastingTypes: Record<string, number>
  } | null>({
    queryKey: ['fastingStats', userId, startDate, endDate],
    queryFn: async () => {
      try {
        const params = new URLSearchParams()
        if (startDate) params.append('startDate', startDate)
        if (endDate) params.append('endDate', endDate)
        
        const response = await apiClient.get(`/fasting/user/${userId}/stats?${params.toString()}`)
        return response.data
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!userId,
  })
}
